import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils-task";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const POLL_MS = 30000;

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch {
      /* silent — auth may be gone */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    timer.current = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer.current);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clickNotif = async (n) => {
    if (!n.read) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((c) => Math.max(0, c - 1));
      } catch {}
    }
    setOpen(false);
    if (n.task_id) navigate("/tasks");
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative h-9 w-9 flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-slate-700" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Notifications</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {unread > 0 ? `${unread} unread` : "You're all caught up"}
            </div>
          </div>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={markAllRead}
              className="text-xs"
              data-testid="notifications-mark-all-read"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {loading && items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">No notifications yet.</div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => clickNotif(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50 ${
                n.read ? "" : "bg-indigo-50/40"
              }`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                    n.read ? "bg-slate-300" : "bg-indigo-500"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                  <div className="text-xs text-slate-600 mt-0.5 leading-relaxed break-words">
                    {n.message}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    {formatDateTime(n.created_at)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
